import { ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

// This is the cn (classNames) utility function that merges Tailwind CSS classes
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Safely sanitize HTML content to remove potentially invalid tags or syntax
export function sanitizeHtml(content: string): string {
  if (!content) return '';
  
  // More comprehensive sanitization function
  return content
    // Fix tags with invalid characters like strongcolors:<
    .replace(/<([a-z0-9]+)colors:[^>]*>/gi, '<$1>')
    // Handle the specific 'strongcolors:' pattern that's causing issues
    .replace(/<strongcolors:[^>]*>/gi, '<strong>')
    // Fix tags with invalid attributes containing colons
    .replace(/<strong[^>]*personalization:[^>]*>/g, '<strong>')
    // Fix unclosed tags or invalid tag formats
    .replace(/<(\/?)([a-z0-9]+):[a-z0-9]+([^>]*)>/gi, '<$1$2$3>')
    // Replace other potentially problematic patterns
    .replace(/<([a-z0-9]+):[a-z0-9]+/gi, '<$1')
    // Handle any other invalid tag patterns that might be causing issues
    .replace(/<([^a-z/!])/gi, '&lt;$1')
    // Fix tags with colors attribute that might be causing problems
    .replace(/<([a-z0-9]+)(\s+)colors:([^>]*)>/gi, '<$1$2>')
    // Remove any attribute containing colors: prefix
    .replace(/\scolors:[^=\s>]+/gi, '')
    // Double check for any remaining invalid tags with colors:
    .replace(/<([^>]*?)colors:([^>]*?)>/gi, '<$1$2>')
    // Fix double quotes in attributes that might be escaped incorrectly
    .replace(/&quot;/g, '"')
    // Fix escaped quote characters
    .replace(/\\"/g, '"')
    // Fix escaped backslashes
    .replace(/\\\\/g, '\\')
    // Fix missing closing tags on td/th elements (common issue in email templates)
    .replace(/<(td|th)([^>]*)>([^<]*)/gi, '<$1$2>$3</$1>')
    // Fix any malformed style attributes
    .replace(/style=(["'])((?:(?!\1).)+)\1/g, (match, quote, styles) => {
      // Ensure each style property ends with semicolon
      const fixedStyles = styles.split(';')
        .map((s: string) => s.trim())
        .filter((s: string) => s)
        .map((s: string) => s + (s.endsWith(';') ? '' : ';'))
        .join(' ');
      return `style=${quote}${fixedStyles}${quote}`;
    });
}

/**
 * Determines if the message content contains an HTML email template
 */
export function messageContainsEmailOrTemplate(content: string): boolean {
  if (!content) return false;
  
  // Check for HTML tags
  const hasHtmlTags = /<html|<body|<table|<div|<p|<h[1-6]|<style|<img|<a/i.test(content);
  
  // Check for common email template patterns
  const hasCommonEmailPatterns = /<!doctype html|<meta|<style>|<header|<footer|<title>|@media/i.test(content);
  
  // If we find multiple indicators, it's likely an email template
  return hasHtmlTags && hasCommonEmailPatterns;
}

export function extractEmailTitle(content: string): string {
  // Try to extract title from HTML content
  let title = '';
  
  // Try to find the subject line in HTML comments or meta tags
  const subjectMatch = content.match(/<!--\s*Subject:\s*(.*?)\s*-->/) || 
                      content.match(/<meta\s+name="subject"\s+content="([^"]+)"/i);
  
  if (subjectMatch && subjectMatch[1]) {
    return subjectMatch[1].trim();
  }
  
  // Try to extract from title tag
  const titleMatch = content.match(/<title>(.*?)<\/title>/i);
  if (titleMatch && titleMatch[1]) {
    return titleMatch[1].trim();
  }
  
  // Try to extract from first heading
  const headingMatch = content.match(/<h1[^>]*>(.*?)<\/h1>/i) || 
                      content.match(/<h2[^>]*>(.*?)<\/h2>/i);
  if (headingMatch && headingMatch[1]) {
    // Remove any HTML tags inside the heading
    return headingMatch[1].replace(/<[^>]*>/g, '').trim();
  }
  
  // If no title found, create one from the first few words of content
  const plainText = content.replace(/<[^>]*>/g, ' ')
                          .replace(/\s+/g, ' ')
                          .trim();
  
  if (plainText) {
    // Use first 50 characters as title
    title = plainText.substring(0, 50).trim();
    if (plainText.length > 50) title += '...';
    return title;
  }
  
  // Default title if nothing else works
  return 'E-Mail Vorlage';
}

export function formatDate(date: Date | string): string {
  if (!date) return '';
  
  const d = typeof date === 'string' ? new Date(date) : date;
  
  return d.toLocaleDateString('de-DE', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR'
  }).format(amount);
}

export function truncateText(text: string, maxLength: number): string {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
}

export function generateUniqueId(): string {
  return Math.random().toString(36).substring(2, 15) + 
         Math.random().toString(36).substring(2, 15);
}

export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  
  return function(...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      func(...args);
    };
    
    if (timeout !== null) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(later, wait);
  };
}

export function isValidEmail(email: string): boolean {
  const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return re.test(email);
}

export function getFileExtension(filename: string): string {
  return filename.slice(((filename.lastIndexOf(".") - 1) >>> 0) + 2);
}

export function isImageFile(filename: string): boolean {
  const ext = getFileExtension(filename).toLowerCase();
  return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(ext);
}

export function isPdfFile(filename: string): boolean {
  const ext = getFileExtension(filename).toLowerCase();
  return ext === 'pdf';
}

export function bytesToSize(bytes: number): string {
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  if (bytes === 0) return '0 Byte';
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return Math.round(bytes / Math.pow(1024, i)) + ' ' + sizes[i];
}
